/**
 * Ranking your first-round slots — `FFL Pass 05 Draft Order.dc.html` 1a (phone) and 1e (desktop),
 * built to `draft-order-brief.md` §2 and §4.
 *
 * WHAT THIS REPLACED, because the deletions are the design:
 *
 *   THREE NUMBERED EXPLAINER CARDS, 142 words of the screen's 505, all of it about one
 *   counterintuitive fact. §1 replaces the lot with a single sentence that lives on the page shell,
 *   and sends the other 141 words to the rulebook behind `§` links. They are not coming back.
 *
 *   A POOL AND A LIST, dragged between. Ranking is now tap-to-add: a tap stamps the slot with the
 *   next number and moves it up; drag handles reorder what is already ranked. One target, one
 *   gesture (§2).
 *
 *   "WHERE THIS LIST LANDS YOU" — a rail card projecting the slot the current list would win.
 *   §4 cuts it and §6 forbids reinstating it "in any form, including a range": before the draw
 *   there is nothing honest to put in it and an estimate reads as a promise. `slotOutlook` is
 *   still in the domain and still tested; this screen no longer calls it.
 *
 * TWO 44px TRAPS, both named in the handoff as easy to reintroduce. The drag handle sits in a 34px
 * row and takes `min-height` with a collapsing negative margin — never an explicit height matched
 * to the row, which is the bug the pattern exists to prevent. `Go to Trades` takes the same
 * treatment rather than being a bare `<a>`.
 *
 * NO PICK-TRADING UI HERE. One pointer card to League › Trades (§4).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dock } from './dock.js';
import { LOTTERY_ODDS } from '../domain/draftOrder.js';
import { countWord, ordinal } from '../domain/lotteryReveal.js';
import { badgeMark, managerShort } from '../domain/identity.js';
import { overallPick } from '../domain/picks.js';
import { Mark } from './avatars.js';
import { AnchorMark, Linkify } from './RuleText.js';
import { HOLD_MS, LIFT_PX, liftsOnHold, numberAtPoint, shouldLift } from './keeper/lift.js';
import { useMinWidth } from './useMinWidth.js';
import { useDockedHeight } from './useDockedHeight.js';
import last from '../data/season-2025.json';
import { LOTTERY_BASIS, placesOf } from '../domain/season2025.js';
import { bothZones } from '../domain/deadlines.js';
import { writeFailed } from './writeFailed.js';
import type { Gate } from '../domain/deadlines.js';
import type { DraftPreference, DraftState, LeagueStore } from '../store/types.js';
import { useLeagueStore } from '../store/provider.js';

const SEASON = 2026;
const TEAMS = 10;
/** Four turns is enough to see the shape of the snake — and it is what the design prints. */
const SHOW_TURNS = 4;
/** Where the rail appears beside the ranking. The keeper path's second threshold, reused. */
const WIDE = 1100;
/** Where the ranking becomes a grid of all ten and the add row goes four across. */
const DESKTOP = 900;
/** Bars are normalised against the biggest weight in the table, not against 100% (§4). */
const ODDS_FULL = 40;
/** Long enough that a burst of taps is one write, short enough to survive closing the tab. */
const AUTOSAVE_MS = 600;
/** How long "Clear all ten?" stands before it goes back to being an offer. */
const ARM_MS = 8000;

const LAST = last as {
  standings: Array<{ gm: string }>;
  thirdPlace: string; fourthPlace: string; toiletBowlWinner: string;
};


/**
 * What a slot actually buys, as pick numbers: "4, 17, 24, 37". Same `overallPick` the board is
 * built from, so the two can never drift.
 */
const picksFor = (slot: number) =>
  Array.from({ length: SHOW_TURNS }, (_, i) => overallPick(i + 1, slot, TEAMS)).join(', ');

/*
 * SLOT 10'S NOTE IS GONE (Pass 06 §6). It read "10, 11, 30, 31 — two picks back to back", which
 * is the only tile in the grid that explained itself; every other slot states four pick numbers
 * and lets the reader see what they mean. Back-to-back picks are visible in "10, 11".
 *
 * THE TWO-COLUMN SPAN SURVIVES FOR NOW, and it is the open question in that section: the span
 * existed to fit the clause, so with the clause gone it may want to collapse to a normal tile.
 * `draftcheck.mjs` asserts `tenSpan === true` and `draft-order-brief.md` §§2 and 6 specify the
 * note — all three move in one commit if Tim says collapse.
 */

interface Props {
  gm: string;
  store: LeagueStore;
  draft: DraftState | null;
  gate: Gate;
  /** Commissioners and admins keep writing after the deadline; managers do not. */
  canOverride: boolean;
  /**
   * How many of the ten have ranked — shown once the draw has run and the question stops being
   * hypothetical. Absent before it, where a count of other people's homework is just pressure.
   */
  sealed?: { ranked: number } | null;
  /**
   * How many ranks cover him, once the draw has said where he chooses (2020-II-1 §4).
   *
   * TWO COUNTERS MUST NOT DISAGREE. The lead card above this one says "six ranks is all you
   * need"; this counter said "rank all ten to be safe" underneath it, which is true only while
   * nobody knows his turn. After the draw it is the one thing on the screen contradicting the
   * rule — and the counter is the thing a GM actually watches.
   */
  need?: number | null;
  /**
   * WHO HAS SENT (17i). The one fact about other people's homework a GM may see: whether it is
   * in. Never a list. Retired once the order publishes, when the trail replaces it.
   */
  roll?: ReadonlyArray<{ gm: string; submittedAt: string | null; updatedAt?: string | null }>;
  /** The seating order, for the roll's sequence. */
  order?: readonly string[];
  /** True once the resolved order is published to the league. */
  published?: boolean;
}

/** `Thu 9:12 PM`, in Eastern time — the short stamp the sent line carries. */
const shortStamp = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' })} ${
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}`;
};
const shortDayOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/New_York' });

export function MyDraftSlot(
  { gm, store, draft, gate, canOverride, sealed, need, roll, order, published = false }: Props,
) {
  const desktop = useMinWidth(DESKTOP);
  const wide = useMinWidth(WIDE);
  /*
   * WHETHER A SUBMISSION CAN REACH THE LEAGUE AT ALL.
   *
   * Matt, 30 August, 10:44 PM: his page had fallen back to the memory store, and this screen
   * wrote his ranking there, stamped it, and showed him "RECORDED · Submitted Sun, Aug 30 at
   * 10:44 PM ET". The league had nothing. The banner at the top of the page said as much, in a
   * paragraph, above a card that said the opposite in a heading. A receipt is a claim about what
   * the LEAGUE holds, and the memory store is not the league — so while the app is on it, this
   * screen does not stamp, does not show a receipt, and does not offer the button.
   */
  const { fellBack } = useLeagueStore();

  const [ranked, setRanked] = useState<number[]>([]);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  /** The write is in flight. Guards a double tap, which would write twice and stamp twice. */
  const [sending, setSending] = useState(false);
  /** What the database said when it refused. Null when nothing has been refused. */
  const [sendFail, setSendFail] = useState<string | null>(null);
  /** The last submission THIS visit — the acknowledgement, which the stamp alone is not. */
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  /**
   * SENT IS A STATE (17i). A ranking that has been sent reads back as a receipt with a "Change
   * it" control, not as a live form: the list is what the league holds, and tapping a slot by
   * accident on a phone should not quietly turn it back into a draft. Change it unlocks; a send
   * locks it again.
   */
  const [changing, setChanging] = useState(false);
  /**
   * THE DRAG, AS POINTER EVENTS.
   *
   * This was HTML5 drag-and-drop on the handle alone, and it was wrong twice over: HTML5 DnD does
   * not exist on touch, so tap-and-drag did nothing at all on the phone this screen is designed
   * for, and a 44px handle is a small target for the one gesture the screen is built around. The
   * keeper path had already worked this out — see the note at the top of `keeper/lift.ts` — and
   * this now uses the same primitives.
   *
   * The whole row is the target (Tim, 21 August). A press that does not travel `LIFT_PX` stays a
   * tap and un-ranks; one that does becomes a lift. `dy` is what the lifted row is translated by,
   * and `over` is where it would land — every other row slides out of the way by one row height,
   * which is the animation rather than a gap being drawn.
   */
  const [drag, setDrag] = useState<
    {
      slot: number; from: number; startX: number; startY: number;
      dx: number; dy: number; over: number; h: number;
    } | null
  >(null);
  /** Set the moment a press becomes a lift, so the row's tap-to-un-rank does not also fire. */
  const lifted = useRef(false);
  /*
   * THE WAY BACK OUT OF THE WHOLE LIST (Tim, 23 August).
   *
   * A single tap un-ranks a slot; there was no way to drop ten of them and think again, and on a
   * phone that is ten deliberate taps in a list that reflows under each one. `remove` is the way
   * out of a mistake and this is the way out of a plan.
   *
   * ARMED, NOT INSTANT. Everything else on this screen is one tap and reversible in one tap;
   * clearing ten ranked slots is neither, so the control asks once. It disarms itself after
   * `ARM_MS` rather than sitting there mid-question — a confirm left standing on a screen a GM
   * scrolled away from is a confirm he presses by accident when he comes back.
   */
  const [arming, setArming] = useState(false);

  const closed = !gate.open && !canOverride;
  const sentLocked = Boolean(submittedAt) && !changing;
  const readOnly = closed || sentLocked;

  useEffect(() => {
    let live = true;
    void store.preferences(SEASON).then((prefs) => {
      if (!live) return;
      const mine = prefs.find((p) => p.gm.toUpperCase() === gm.toUpperCase());
      setRanked(mine?.ranked ?? []);
      setSubmittedAt(mine?.submittedAt ?? null);
      setLoaded(true);
    });
    return () => { live = false; };
  }, [store, gm]);

  /*
   * AUTOSAVE, WHICH IS WHY SUBMIT IS NOT SAVE.
   *
   * §2: "Rankings autosave as they change — the button submits, it does not save." That split is
   * also why `DraftPreference` grew `submittedAt`: once the first tap writes, "has a ranking" stops
   * meaning "has submitted", and the commissioner's non-submitter warning needs the second meaning
   * before it runs an irreversible draw.
   *
   * Debounced, and skipped until the first load has landed — without the guard the empty array a
   * fresh mount starts with would be written straight over a saved list.
   */
  const written = useRef<string | null>(null);
  useEffect(() => {
    if (!loaded || readOnly) return undefined;
    const key = ranked.join(',');
    if (written.current === null) { written.current = key; return undefined; }
    if (written.current === key) return undefined;
    const id = window.setTimeout(() => {
      written.current = key;
      void store.savePreference({
        season: SEASON, gm, ranked, submittedAt, updatedAt: new Date().toISOString(),
      });
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(id);
  }, [ranked, loaded, readOnly, store, gm, submittedAt]);

  /* An edit after a send is a draft again, so the acknowledgement must not outlive it. */
  useEffect(() => {
    if (sentAt && ranked.join(',') !== written.current) { setSentAt(null); setSendFail(null); }
  }, [ranked, sentAt]);

  const unranked = useMemo(
    () => Array.from({ length: TEAMS }, (_, i) => i + 1).filter((s) => !ranked.includes(s)),
    [ranked],
  );

  const add = useCallback((slot: number) => {
    if (readOnly) return;
    setRanked((prev) => (prev.includes(slot) ? prev : [...prev, slot]));
  }, [readOnly]);

  /*
   * The way back out of a mistap. Tim's call, 21 August: tap-to-add with no way to un-rank makes a
   * fat finger unrecoverable, so the row's badge-and-name area is the button that sends the slot
   * home. Nothing new appears on the row — the design's 34px row is untouched — and the handle
   * keeps its own 44px target beside it.
   */
  const remove = useCallback((slot: number) => {
    if (readOnly) return;
    setRanked((prev) => prev.filter((s) => s !== slot));
  }, [readOnly]);

  /*
   * Empty the list. The autosave below writes it like any other change, so this does not save and
   * does not touch `submittedAt` — a GM who has already submitted and then starts again is still
   * submitted, with a list that is being rebuilt. §2 keeps those two facts separate everywhere
   * else on this screen and they stay separate here.
   */
  const startOver = useCallback(() => {
    if (readOnly) return;
    setArming(false);
    setRanked([]);
  }, [readOnly]);

  /* A question nobody answered goes back to being an offer. */
  useEffect(() => {
    if (!arming) return undefined;
    const id = window.setTimeout(() => setArming(false), ARM_MS);
    return () => window.clearTimeout(id);
  }, [arming]);

  /* Nothing left to clear, or no longer allowed to: the question cannot outlive its subject. */
  useEffect(() => {
    if (ranked.length === 0 || readOnly) setArming(false);
  }, [ranked.length, readOnly]);

  const move = useCallback((slot: number, toIndex: number) => {
    if (readOnly) return;
    setRanked((prev) => {
      const from = prev.indexOf(slot);
      if (from < 0) return prev;
      const next = prev.filter((s) => s !== slot);
      // The index came from a list that still held the row, so anything below it is one place out.
      const to = toIndex > from ? toIndex - 1 : toIndex;
      next.splice(Math.max(0, Math.min(to, next.length)), 0, slot);
      return next;
    });
  }, [readOnly]);

  /**
   * SUBMIT, AND SAY WHICH OF THE THREE THINGS HAPPENED.
   *
   * Tim, 31 August: "resubmit button isn't working." It was doing two things that between them
   * make a working button indistinguishable from a broken one:
   *
   *   IT STAMPED FIRST AND WROTE SECOND. `setSubmittedAt(at)` ran before the write, so the
   *   receipt said "Submitted just now" whether or not anything reached the database — and the
   *   old stamp came back on the next reload with nothing on screen having admitted anything. It
   *   is the same shape as the lottery's `record` bug, on the screen next door.
   *
   *   AND IT SAID NOTHING ON SUCCESS. Resubmitting an unchanged list moves one timestamp inside a
   *   card he is not looking at. There is now a plain acknowledgement in the bar his finger is
   *   already on.
   *
   * So: write, THEN stamp; surface the refusal in words a commissioner can act on; and confirm.
   */
  async function submit() {
    if (sending) return;
    if (fellBack) {
      setSendFail('Not sent — this page is not connected to the league. Reload and try again.');
      return;
    }
    setSending(true);
    setSendFail(null);
    const at = new Date().toISOString();
    try {
      await store.savePreference({
        season: SEASON, gm, ranked, submittedAt: at, updatedAt: at,
      });
      written.current = ranked.join(',');
      setSubmittedAt(at);
      setSentAt(at);
      setChanging(false);
    } catch (err) {
      setSendFail(writeFailed(err));
    } finally {
      setSending(false);
    }
  }

  /**
   * WHICH PLACE SETS HIS WEIGHT, which since Pass 06 §1 is not always where he finished.
   *
   * Places 1-6 are playoff outcomes and places 7-10 are the regular season, so Samant is 7th by
   * the order of finish and 8th by the table the odds are keyed to. The card below says which one
   * it is quoting, because "you finished 9th, which is a 10% weight" is two true facts that do not
   * belong in one sentence.
   */
  const finishOrder = draft?.finishOrder?.length === TEAMS ? draft.finishOrder : LOTTERY_BASIS;
  const finish = useMemo(() => {
    const i = finishOrder.findIndex((g) => g.toUpperCase() === gm.toUpperCase());
    return i < 0 ? null : i + 1;
  }, [finishOrder, gm]);

  /*
   * The bar publishes its own height so the page can reserve it. Stating the geometry in CSS was
   * right until a copy change made the sentence wrap — see `useDockedHeight.ts`.
   */
  const commitRef = useRef<HTMLDivElement>(null);
  useDockedHeight(commitRef);

  /*
   * EMPTY IS NOT PARTIAL (17o). A partial ranking is legal and the button never gates on how many
   * slots are ranked — but a list with nothing on it is not a ranking, and sending one stamps a
   * submission the resolve will read as "no list". Disabled until at least one slot is ranked.
   */
  const nothingRanked = ranked.length === 0;
  /** One string, two places: the button and the sentence pointing at it cannot disagree. */
  const submitLabel = submittedAt ? 'Resubmit ranking' : 'Submit ranking';

  /*
   * THE BAR'S ONE LINE, AND THE TWO STATES IT WAS MISSING. It reported autosave and nothing else,
   * so pressing the button produced no reply of any kind — which is what "resubmit button isn't
   * working" looks like from the outside whether or not the write landed. It now acknowledges a
   * success and quotes a refusal, in the place his finger already is.
   */
  const recorded = sentAt
    ? `Recorded ${bothZones(sentAt)}. Changes autosave as a draft until you submit again.`
    : '';
  const barSays = !loaded ? 'Loading…'
    : sendFail ? sendFail
      : fellBack ? 'Not connected to the league — nothing here will be recorded'
      : closed ? 'Ranking is closed. This is the list we will use — if it is wrong, tell a '
      + 'commissioner; it can still be corrected before the order is published.'
        : sentLocked ? `Sent ${shortStamp(submittedAt!)}. Change it above to send a different list.`
        : ranked.length === 0 ? 'Rank at least one slot to send.'
        : recorded
          || `Ranking updates autosave as a draft. Click ${submitLabel} to finalize your choices.`;

  /** His two places, for the one sentence that has to name which of them it is quoting. */
  const mine = useMemo(() => placesOf(gm), [gm]);

  /* Covered, not exhaustive: ten is only the requirement while his turn is unknown. */
  const complete = ranked.length >= (need ?? TEAMS);

  const claims = [
    draft?.toiletBowlWinner ?? LAST.toiletBowlWinner
      ? {
        gm: draft?.toiletBowlWinner ?? LAST.toiletBowlWinner,
        what: 'Toilet Bowl',
        says: 'Chooses second unless he wins the lottery outright.',
        anchor: 'toilet-bowl-priority',
      }
      : null,
    draft?.thirdPlace ?? LAST.thirdPlace
      ? {
        gm: draft?.thirdPlace ?? LAST.thirdPlace,
        what: 'third place',
        /*
         * The old line described the SUPERSEDED mechanic — a swap of picks — and "declarable
         * after the lottery" is redundant now the header says it.
         *
         * NAMED, not "fourth" (Tim, 25 August). The card already carries Ari's face and his name;
         * saying the other party is "fourth" makes the reader go and look up who that is, on a
         * card whose whole job is to say who can do what to whom.
         */
        says: `May take ${managerShort(draft?.fourthPlace ?? LAST.fourthPlace)}’s lottery result `
          + 'instead of his own.',
        anchor: 'third-place-swap',
      }
      : null,
  ].filter((c): c is NonNullable<typeof c> => c !== null);

  /** The positions the ranking draws: every one of them on a desktop, only the filled ones on a phone. */
  /*
   * ONE COLUMN, EVERYWHERE (Tim, 27 August: "the two-column layout is confusing").
   *
   * From 900px this list used to flow into two columns of five, and the ten slots then existed in
   * two places at once — positions 1-5 beside positions 6-10, with a SEPARATE grid of unranked
   * tiles under both. Three groupings of one set of ten.
   *
   * It also crippled the drag, which is the other half of what was wrong. Neighbours could not
   * step aside on a two-column grid — "move down by one row height" is the wrong move when the
   * next position is to the RIGHT — so on the desktop the only feedback a drag had was a border
   * on the target row. One column restores it, and the empty placeholder positions that existed
   * only to fill the second column go with it.
   */
  const positions = ranked.map((_, i) => i + 1);

  /*
   * Where a lifted row would land, read from the row actually under the pointer.
   *
   * BOTH COORDINATES COME FROM THE POINTER. The first version asked at the viewport's horizontal
   * midpoint, which is inside the list on a phone and inside the odds rail on a desktop — so on a
   * desktop it found no row, kept the starting index, and every mouse drag dropped exactly where
   * it began. It looked like the drag was not working at all; it was landing perfectly on the
   * spot it started from.
   *
   * The lifted row is `pointer-events: none`, so this resolves the row underneath rather than the
   * one in the air — the same reason `roundAtPoint` gives next door.
   */
  const overFrom = (clientX: number, clientY: number, fallback: number): number => {
    const at = numberAtPoint(document, clientX, clientY, 'data-rank');
    if (at === null) return fallback;
    /*
     * CLAMPED TO THE LIST. The desktop draws all ten positions, so a row can be dragged over
     * position 8 while only five slots are ranked — and there is no eighth place to be. The
     * landing is the end of the list, which is what dropping past the last row means anywhere.
     */
    return Math.max(0, Math.min(at, Math.max(0, ranked.length - 1)));
  };

  function beginDrag(e: React.PointerEvent, slot: number, index: number) {
    if (readOnly || e.button > 0) return;
    const row = (e.currentTarget as HTMLElement);
    const h = Math.round(row.getBoundingClientRect().height);
    const startX = e.clientX;
    const startY = e.clientY;
    lifted.current = false;
    /*
     * CAPTURE ON THE ROW. Without it the pointer leaves the element the moment the row is
     * translated out from under it and every subsequent move is delivered somewhere else — which
     * is the mouse half of "drag does not work".
     */
    row.setPointerCapture(e.pointerId);

    /*
     * TWO WAYS IN, because a finger and a mouse are competing with different things.
     *
     * A mouse lifts as soon as it has travelled `LIFT_PX` — nothing else wants that gesture. A
     * finger has to HOLD first, because the identical downward movement is how the page scrolls,
     * and a list that lifts on movement is a list you cannot scroll past. Once the hold fires,
     * `touch-action: none` goes on the row so the browser stops competing for the rest of it.
     */
    const byHold = liftsOnHold(e.pointerType);
    const start = (dx: number, dy: number) => {
      lifted.current = true;
      if (byHold) row.style.touchAction = 'none';
      setDrag({ slot, from: index, startX, startY, dx, dy, over: index, h });
    };
    const timer = byHold ? window.setTimeout(() => start(0, 0), HOLD_MS) : 0;

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!lifted.current) {
        // Moved before the hold fired: this is a scroll, not a lift. Let the page have it.
        if (byHold) { if (shouldLift(dx, dy, LIFT_PX)) window.clearTimeout(timer); return; }
        if (!shouldLift(dx, dy, LIFT_PX)) return;
        start(dx, dy);
      }
      ev.preventDefault();
      setDrag((d) => (d ? { ...d, dx, dy, over: overFrom(ev.clientX, ev.clientY, d.over) } : d));
    };
    const onEnd = (ev: PointerEvent) => {
      window.clearTimeout(timer);
      row.style.touchAction = '';
      row.releasePointerCapture?.(ev.pointerId);
      row.removeEventListener('pointermove', onMove);
      row.removeEventListener('pointerup', onEnd);
      row.removeEventListener('pointercancel', onEnd);
      setDrag((d) => {
        if (d && lifted.current) {
          // `move` splices into a list that still holds the row, so a downward landing is one on.
          move(d.slot, d.over > d.from ? d.over + 1 : d.over);
        }
        return null;
      });
      /* Cleared after the click that follows the release, not before it. */
      window.setTimeout(() => { lifted.current = false; }, 0);
    };
    row.addEventListener('pointermove', onMove);
    row.addEventListener('pointerup', onEnd);
    row.addEventListener('pointercancel', onEnd);
  }

  const rankedRow = (index: number) => {
    const slot = ranked[index];
    if (slot === undefined) {
      return (
        /*
         * AN EMPTY POSITION IS STILL A DROP TARGET. It had no `data-rank`, so `numberAtPoint`
         * found nothing over it and the drag kept its previous target — and since the whole right
         * column is empty positions until ten are ranked, that is precisely "dragging from one
         * column to the other does nothing". Reported 21 August.
         */
        <li key={`empty-${index}`} data-rank={index} className="dolrrow empty">
          <span className="dolrbadge">{index + 1}</span>
          <span className="dolrempty">empty</span>
        </li>
      );
    }
    const isHeld = drag?.slot === slot;
    /*
     * EVERY OTHER ROW STEPS ASIDE. A row between where the lifted one came from and where it is
     * going shifts by exactly one row height, so the list shows the answer before the finger
     * lets go. The transition is on the class, not here, so the lifted row itself is not eased —
     * it has to track the pointer exactly.
     */
    /*
     * AND NOW THEY STEP ASIDE AT EVERY WIDTH. This was guarded on `!desktop` because the desktop
     * was two columns and a vertical shift could not describe a move across them; the guard was
     * the reason a desktop drag showed almost nothing. One column, one honest animation.
     */
    let shift = 0;
    if (drag && !isHeld) {
      if (drag.from < index && index <= drag.over) shift = -drag.h;
      else if (drag.over <= index && index < drag.from) shift = drag.h;
    }
    return (
      <li
        key={slot}
        data-rank={index}
        className={[
          'dolrrow',
          isHeld ? 'lifted' : '',
          /*
            AND THE EASING TOO. The transform above was un-guarded on 27 August and this class was
            not, so a desktop row jumped to its new place instead of sliding — the move happened
            and looked like a repaint. Both halves of "the rows step aside" are one decision.
          */
          drag && !isHeld ? 'shifting' : '',
        ].filter(Boolean).join(' ')}
        style={isHeld
          // BOTH AXES. The lift has to be able to cross columns, not just rows.
          ? { transform: `translate(${drag!.dx}px, ${drag!.dy}px)` }
          : shift ? { transform: `translateY(${shift}px)` } : undefined}
        onPointerDown={(e) => beginDrag(e, slot, index)}
      >
        <span className="dolrbadge">{index + 1}</span>
        {/*
          A press that never travelled is a tap, and a tap un-ranks. The flag is set by the lift,
          so the drag that ends over this row does not also delete it.
        */}
        <button type="button" className="dolrbody" disabled={readOnly}
          aria-label={`Remove Slot ${slot} from your ranking`}
          onClick={() => { if (!lifted.current) remove(slot); }}>
          <span className="dolrname">Slot {slot}</span>
          <span className="dolrpicks">{picksFor(slot)}</span>
        </button>
        {/*
          TWO BUTTONS, AND THEY ARE THE PRIMARY WAY (Tim, 27 August: "dragging barely works" —
          and not to copy the keeper strip's lift, which is not perfect either).

          A drag has to be discovered, aimed and released, and on a phone it competes with the
          scroll; it fails differently on every device. Moving a row one place is one tap that
          cannot miss, works with a mouse, a finger and a keyboard, and needs no affordance to
          teach. The drag still works for anybody who reaches for it — it is no longer the only
          way to reorder, which is what made it "barely works" rather than "an extra".

          `onPointerDown` stops here, or pressing an arrow would begin a lift on the row under it.
        */}
        <span className="dolnudge" onPointerDown={(e) => e.stopPropagation()}>
          <button type="button" className="dolarrow" disabled={readOnly || index === 0}
            aria-label={`Move Slot ${slot} up to position ${index}`}
            onClick={() => move(slot, Math.max(0, index - 1))}>
            <span aria-hidden="true">&#9650;</span>
          </button>
          <button type="button" className="dolarrow"
            disabled={readOnly || index === ranked.length - 1}
            aria-label={`Move Slot ${slot} down to position ${index + 2}`}
            onClick={() => move(slot, Math.min(ranked.length, index + 2))}>
            <span aria-hidden="true">&#9660;</span>
          </button>
        </span>
      </li>
    );
  };

  return (
    <div className={`dolwrap${wide ? ' wide' : ''}`}>
      {/*
        NOT CONNECTED, SAID WHERE HE IS RANKING. Nothing typed on a page that cannot reach the
        league is recorded, and a form that looks like it is working is worse than one that says
        it is not.
      */}
      {fellBack && (
        <section className="dolcard dolreceipt bad">
          <div className="dolcardhd">
            <span className="dolcaps">Not connected</span>
          </div>
          <p className="dolmut">
            This page cannot reach the league right now, so nothing you rank here is recorded and
            no receipt is shown. Reload once you have a connection, then submit.
          </p>
        </section>
      )}

      {/*
        THE RECEIPT CARD IS GONE (Tim, 7 September). Pass 06 §7 put what the league had recorded
        on the page in its own card — the stamp and the ten slots again — at a time when the
        ranking card said nothing about having been sent. It says it now: `YOUR RANKING — sent
        Mon 7:30 AM`, over the same ten rows, and the cure-window sentence rides the bar at the
        foot of the card. Two lists of the same ten slots, one above the other, was the page
        telling him twice and agreeing with itself.
      */}
      <section className="dolcard dolrank">
        {/*
          One counter, two wordings. §2 calls the count "the whole progress model" — amber until it
          reads ten, then ink, and nothing else on the screen reports validity.
        */}
        <div className="dolrankhd">
          <span className="dolcaps">
            {desktop ? 'Your ranking' : 'Ranked'}
            {submittedAt && <span className="dolsentat"> — sent {shortStamp(submittedAt)}</span>}
          </span>
          {sentLocked && !closed && (
            <button type="button" className="dolchange" onClick={() => setChanging(true)}>
              Change it
            </button>
          )}
          <span className="dolranksub">
            {/*
              THE ARROWS ARE THE INSTRUCTION NOW. "Drag to reorder" named the one mechanism that
              could fail, and named it as the only one.
            */}
            {desktop ? 'best first · arrows or drag to reorder'
              : need ? `${countWord(need).toLowerCase()} covers you`
                : 'rank all ten to be safe'}
          </span>
          <span className={`dolnum${complete ? ' done' : ''}`}>
            {ranked.length}
            <span className="dolnumof">
              {' '}of {need ?? TEAMS}{desktop ? ' ranked' : ''}
            </span>
          </span>
        </div>

        {/*
          ONE LIST OF TEN. What ranked and unranked slots are is a property of a row, not a reason
          for two containers — the separate "click to add" grid underneath meant the same ten
          objects appeared twice on one screen, in two shapes, with two interactions.

          The order of this list IS the answer: your ranking, best first, and then everything you
          have not ranked in slot order behind a line saying what happens to them.
        */}
        <ol className="dolone">
          {positions.map((_, i) => rankedRow(i))}

          {unranked.length > 0 && (
            <li className="dolcut" aria-hidden="true">
              <span>
                {ranked.length === 0
                  ? 'Tap one to start your list'
                  : 'Not ranked — you would take the earliest of these still free'}
              </span>
            </li>
          )}

          {unranked.map((slot) => (
            <li key={`u-${slot}`} className="dolrrow unranked">
              <span className="dolrbadge empty" aria-hidden="true" />
              <button type="button" className="dolrbody" disabled={readOnly}
                aria-label={`Add Slot ${slot} to your ranking at position ${ranked.length + 1}`}
                onClick={() => add(slot)}>
                <span className="dolrname">Slot {slot}</span>
                <span className="dolrpicks">{picksFor(slot)}</span>
              </button>
              <span className="dolnudge">
                <button type="button" className="dolarrow add" disabled={readOnly}
                  aria-label={`Add Slot ${slot}`}
                  onClick={() => add(slot)}>
                  <span aria-hidden="true">+</span>
                </button>
              </span>
            </li>
          ))}
        </ol>

        {unranked.length === 0 && (
          <p className="dolallin">All ten ranked, in the order above.</p>
        )}

        <div className="doladd">
          {/*
            Under the slots rather than up beside the counter: §2 gives that header to the count
            and calls it "the whole progress model", and a destructive control sharing a line with
            the thing it destroys reads as part of it. Only shown when there is something to clear.
          */}
          {ranked.length > 0 && !readOnly && (
            <div className="dolresetrow">
              {arming ? (
                <>
                  <span className="dolresetask" role="status">
                    {ranked.length === 1
                      ? 'Clear the one slot and start again?'
                      : `Clear all ${countWord(ranked.length).toLowerCase()} and start again?`}
                  </span>
                  <button type="button" className="dolreset danger" onClick={startOver}>
                    Clear
                  </button>
                  <button type="button" className="dolreset" onClick={() => setArming(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="dolreset" onClick={() => setArming(true)}>
                  Start over
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <aside className="dolrail">
        {/*
          SEALED, WITH THE COUNT SHOWN — the handoff's settled model. A GM needs to know the
          deadline is real and that other people are moving; he does not need, and must not have,
          anybody else's list. The count is the whole of what can be said without turning the
          ranking into an auction.
        */}
        {sealed && (
          <section className="dolcard dolsealed">
            <div className="dolcardhd">
              <span className="dolcaps">Rankings are sealed</span>
              <span className="dolsealcount">{sealed.ranked} of {TEAMS}</span>
            </div>
            <p className="doloddsnote">
              {countWord(sealed.ranked)} of the ten have ranked. Nobody sees another
              GM&rsquo;s list &mdash; only how many are in.
            </p>
          </section>
        )}

        {/*
          The odds chart earns its place because it is the one thing on the page a GM cannot work
          out for himself. Every value comes from `LOTTERY_ODDS`; the bars are normalised against
          40%, so the biggest weight fills the track and 20% fills half of it (§4).
        */}
        <section className="dolcard dolodds">
          <div className="dolcardhd">
            <span className="dolcaps">Lottery odds</span>
            <AnchorMark anchor="lottery-odds" size="sm" />
          </div>
          <Linkify>
            {/*
              WHICH TABLE, SAID OUT LOUD. Pass 06 §1 keys places 1-6 to the playoff bracket and
              7-10 to the regular season, so four GMs sit at a different place here than they do
              in the standings — and the sentence has to name the one it means or it reads as the
              app getting somebody's season wrong. Only said where the two actually disagree;
              everybody in the bracket has one place and does not need the distinction.
            */}
            <p className="doloddsnote">
              {finish === null
                ? 'No 2025 finish on file for you yet, so there is no weight to show.'
                : `You were ${finish}${ordinal(finish)} ${mine.final === mine.regular
                  ? 'last season' : 'in the regular season'}, which is a `
                  + `${LOTTERY_ODDS[finish] ?? 0}% weight in the draw.`}
            </p>
            {finish !== null && mine.final !== mine.regular && (
              <p className="doloddsnote mut">
                You finished {mine.final}{ordinal(mine.final)} once the consolation bracket was
                played. The lottery is weighted on the regular season below the playoff places.
              </p>
            )}
          </Linkify>
          <ul className="dolbars">
            {Array.from({ length: TEAMS }, (_, i) => i + 1).map((place) => {
              const pct = LOTTERY_ODDS[place] ?? 0;
              /*
               * WHOSE PLACE IT IS. The ladder is fixed by rule — 1st through 10th, each with the
               * weight 2014-II-1 §1 gives it — but a column of ordinals asks the reader to hold
               * the standings in his head to find himself or anybody else in it. `finishOrder` is
               * already resolved above for exactly that lookup, so the face and the name go where
               * the question is: whose 40% is that.
               */
              const who = finishOrder[place - 1];
  return (
                <li key={place} className={place === finish ? 'mine' : ''}>
                  <span className="dolbarpos">{place}{ordinal(place)}</span>
                  {who && (
                    <Mark gm={who} className="dolbarmark">{badgeMark(who)}</Mark>
                  )}
                  {who && <span className="dolbarname">{managerShort(who)}</span>}
                  <span className="dolbartrack" aria-hidden="true">
                    <i style={{ width: `${(pct / ODDS_FULL) * 100}%` }} />
                  </span>
                  <span className="dolbarpct">{pct}%</span>
                </li>
              );
            })}
          </ul>
        </section>

        {/* 17i: who has sent — the count with names, never a list — until the order publishes. */}
        {roll && roll.length > 0 && !published && (() => {
          const seq = order && order.length ? order : roll.map((r) => r.gm);
          const me = gm.toUpperCase();
          const byGm = new Map(roll.map((r) => [r.gm.toUpperCase(), r]));
          const sent = seq.filter((g) => g.toUpperCase() !== me && byGm.get(g.toUpperCase())?.submittedAt);
          const not = seq.filter((g) => g.toUpperCase() !== me && !byGm.get(g.toUpperCase())?.submittedAt);
          const revised = sent.filter((g) => {
            const r = byGm.get(g.toUpperCase())!;
            return r.updatedAt && r.submittedAt && r.updatedAt > r.submittedAt;
          });
          const plain = sent.filter((g) => !revised.includes(g));
          return (
            <section className="dolcard dolroll">
              <div className="dolcardhd">
                <span className="dolcaps">Who has sent</span>
                <span className="dolrollsub">until the order publishes</span>
              </div>
              <ul className="dolrolllist">
                {revised.map((g) => {
                  const r = byGm.get(g.toUpperCase())!;
                  return (
                    <li key={g}>
                      {managerShort(g)} — sent {shortDayOf(r.submittedAt!)} · revised {shortDayOf(r.updatedAt!)}
                    </li>
                  );
                })}
                <li className="me">
                  <b>You — {submittedAt ? `sent ${shortStamp(submittedAt)}` : 'nothing yet'}</b>
                </li>
                {plain.length > 0 && <li>{plain.map(managerShort).join(', ')} — sent</li>}
                {not.length > 0 && <li className="not">{not.map(managerShort).join(', ')} — nothing yet</li>}
              </ul>
            </section>
          );
        })()}
        {!published && (
          <section className="dolcard doltrailghost">
            <div className="dolcardhd">
              <span className="dolcaps">The trail</span>
              <span className="dolrollsub">replaces it after publication</span>
            </div>
            <p className="doloddsnote mut">
              What a ranking did, one line per GM, yours marked — never another man&rsquo;s list.
            </p>
          </section>
        )}

        {/*
          These change who can take your slot before your turn, so they belong beside the ranking
          rather than in the rules (§4) — and on the phone as well as the desktop, which is Tim's
          call of 21 August against a design that had them on one breakpoint only.
        */}
        {claims.length > 0 && (
          <section className="dolcard dolclaims">
            <span className="dolcaps">
              {/*
                AFTER, not before (Pass 06 §6). Both claims are exercised once the lottery has
                run — the Toilet Bowl lift is applied to the drawn order, and the third-place
                option is a declaration against a result that does not exist until the draw.
              */}
              {countWord(claims.length)} GM{claims.length === 1 ? ' has' : 's have'} a claim
              after the draw
            </span>
            <ul>
              {claims.map((c) => (
                <li key={c.anchor}>
                  <Mark gm={c.gm} className="dolclaimmark">{badgeMark(c.gm)}</Mark>
                  <span className="dolclaimtext">
                    <b>{managerShort(c.gm)} — {c.what}</b>
                    <span>{c.says}</span>
                  </span>
                  <AnchorMark anchor={c.anchor} size="sm" />
                </li>
              ))}
            </ul>
          </section>
        )}

      </aside>

      {/*
        Pinned. Autosave means the bar reports rather than acts, and the button submits — §2 is
        explicit that it does not save.
      */}
      {/*
        PORTALLED TO THE BODY. This bar is `position: fixed`, so the viewport is meant to place
        it — but a fixed element is placed by the nearest ancestor carrying a transform, and
        pull-to-refresh translates the page content. Left in the tree it would anchor to the
        bottom of the whole page the moment somebody pulled, and slide off the screen. Its CSS
        never assumed an ancestor, so nothing else changes.
      */}
      {dock(
        <div className="dolcommit" ref={commitRef}>
              {/*
                THE SAME SENTENCE IN BOTH STATES (Pass 06 §6). It used to read "Submitted — still saving
                as you change it" once sent, which describes the autosave and hides the model: an edit
                after submitting IS a draft again until it is re-submitted, because `submittedAt` is a
                separate fact from the ranking. Saying so in both states is the honest version, and the
                submitted fact lives in the status pill on the card above rather than down here.

                The button's own label is interpolated rather than typed out, so the sentence cannot
                drift from the control it is telling you to press.
              */}
              <span className={`dolsaved${sendFail || fellBack ? ' bad' : sentAt ? ' sent' : ''}`}>
                {barSays}
              </span>
              <button type="button" className="dolsubmit"
                disabled={readOnly || !loaded || sending || Boolean(fellBack) || nothingRanked}
                onClick={() => void submit()}>
                {sending ? 'Submitting…' : submitLabel}
              </button>
            </div>,
      )}
    </div>
  );
}

/** Kept for the preferences read in `DraftPosition` — one shape, one place it is counted. */
export const hasSubmitted = (p: DraftPreference): boolean => Boolean(p.submittedAt);
